import React from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import BasketPurchase from "./BasketPurchase";
import useBasketDetails from "./useBasketDetails";
import { useShare } from "../../hooks/useShare";
import { FiImage } from "react-icons/fi";
import { formatLek } from "../../Utils/currency";
import {
  isLowAvailability,
  isSoldOut,
  readAvailability,
} from "../../Utils/availability";
import "./BasketPage.css";

const BasketPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const share = useShare();
  const { basket, farm, relatedBaskets, loading, error, retry } = useBasketDetails(id);

  const available = readAvailability(basket);
  const soldOut = isSoldOut(available);

  if (loading) {
    return (
      <div className="basket-page">
        <div className="basket-page-container" aria-busy="true">
          <div className="bp-skeleton-image" />
          <div className="bp-skeleton-info">
            <div className="bp-skeleton-line" style={{ width: "60%", height: 28 }} />
            <div className="bp-skeleton-line" style={{ width: "30%", height: 20 }} />
            <div className="bp-skeleton-line" style={{ width: "100%", height: 60 }} />
            <div className="bp-skeleton-line" style={{ width: "40%", height: 44 }} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="basket-page">
        <div className="basket-page-container bp-not-found" role="alert">
          <h2>{t("baskets.load_error", { defaultValue: "We couldn't load this basket." })}</h2>
          <p>{t("baskets.load_error_hint", { defaultValue: "Check your connection and try again." })}</p>
          <button type="button" className="bp-back-btn" onClick={retry}>
            {t("baskets_page.retry", { defaultValue: "Try again" })}
          </button>
        </div>
      </div>
    );
  }

  if (!basket) {
    return (
      <div className="basket-page">
        <div className="basket-page-container bp-not-found">
          <h2>{t("baskets.not_found", { defaultValue: "Basket not found" })}</h2>
          <p>{t("baskets.not_found_desc", { defaultValue: "This basket may have been removed or doesn't exist." })}</p>
          <button className="bp-back-btn" onClick={() => navigate("/farms")}>
            {t("baskets.browse_farms", { defaultValue: "Browse Farms" })}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="basket-page">
      <div className="basket-page-container">
        <div className="bp-breadcrumb">
          <Link to="/farms">{t("nav.farms", { defaultValue: "Farms" })}</Link>
          {farm && (
            <>
              <span> / </span>
              <Link to={`/farms/${farm.id}`}>{farm.name}</Link>
            </>
          )}
          <span> / </span>
          <span>{basket.name}</span>
        </div>

        <div className="bp-main">
          <div className="bp-image-section">
            {basket.image ? (
              <img
                src={basket.image}
                alt={basket.name}
                className="bp-image"
                loading="lazy"
              />
            ) : (
              <div className="bp-image-fallback" aria-hidden="true"><FiImage /></div>
            )}
          </div>

          <div className="bp-info-section">
            <h1 className="bp-title">{basket.name}</h1>

            <div className="bp-price">
              {formatLek(basket.price)}
            </div>

            {soldOut ? (
              <p className="bp-availability out">
                {t("baskets.sold_out_detail", {
                  defaultValue: "Sold out — this farm is out of an ingredient.",
                })}
              </p>
            ) : isLowAvailability(available) ? (
              <p className="bp-availability low">
                {t("baskets.only_left", {
                  count: available,
                  defaultValue: "Only {{count}} left",
                })}
              </p>
            ) : available !== null ? (
              <p className="bp-availability">
                {t("baskets.available_count", {
                  count: available,
                  defaultValue: "{{count}} available",
                })}
              </p>
            ) : null}

            {(basket.isHot || basket.isNew) && (
              <div className="bp-badges">
                {basket.isHot && <span className="bp-badge hot">{t("baskets.popular", { defaultValue: "Popular" })}</span>}
                {basket.isNew && <span className="bp-badge new">{t("baskets.new", { defaultValue: "New" })}</span>}
              </div>
            )}

            <p className="bp-description">{basket.description}</p>

            {basket.items.length > 0 && (
              <>
                <h3 className="bp-items-title">
                  {t("baskets.whats_inside", { defaultValue: "What's Inside" })}
                </h3>
                <div className="bp-items-list">
                  {basket.items.map((item) => (
                    <span key={item.productName || item.productId} className="bp-item-chip">
                      {item.productName}
                      <span className="bp-item-qty">×{item.quantity}</span>
                    </span>
                  ))}
                </div>
              </>
            )}

            {farm && (
              <div className="bp-farm-info">
                <Link to={`/farms/${farm.id}`} className="bp-farm-link">
                  {farm.logoImageUrl && (
                    <img src={farm.logoImageUrl} alt={farm.name} className="bp-farm-logo" loading="lazy" />
                  )}
                  <span>{farm.name}</span>
                </Link>
                {farm.location && <span className="bp-farm-location">{farm.location}</span>}
              </div>
            )}

            <BasketPurchase key={basket.id} basket={basket} farm={farm} />

            <button
              className="bp-share-btn"
              type="button"
              onClick={() => share({
                title: basket.name,
                text: t("baskets.share_text", { name: basket.name, defaultValue: "See {{name}} on Dora e Fshatit." }),
                url: window.location.href,
              })}
            >
              {t("baskets.share", { defaultValue: "Share" })}
            </button>
          </div>
        </div>

        {relatedBaskets.length > 0 && (
          <div className="bp-related">
            <h2 className="bp-related-title">
              {t("baskets.more_baskets", { defaultValue: "More Baskets" })}
            </h2>
            <div className="bp-related-grid">
              {relatedBaskets.map((rb) => (
                <Link
                  to={`/baskets/${rb.id}`}
                  key={rb.id}
                  className="bp-related-card"
                >
                  {rb.image ? (
                    <img src={rb.image} alt={rb.name} loading="lazy" />
                  ) : (
                    <div className="bp-related-card-fallback" aria-hidden="true"><FiImage /></div>
                  )}
                  <h3>{rb.name}</h3>
                  <p>{formatLek(rb.price)}</p>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BasketPage;
